/**
 * The one read a pre-consultation form makes: whose form this is, what its
 * dropdowns offer, and whether the link is still good for anything.
 *
 * Hand-rolled rather than a `useQuery`, exactly as `usePublicEnquiryForm` is
 * and for the same reason — TanStack's provider is mounted by the dashboard's
 * tree and this screen renders outside all of it. Nothing here may assume a
 * provider: no query client, no toasts, no Clerk, no workspace.
 *
 * Five outcomes, and the page draws a different thing for each:
 *   loading    the read is still out
 *   ready      the form, with the brand to head it
 *   completed  this link has already been filled in
 *   missing    expired, or never valid — one state, deliberately
 *   failed     anything else: offline, a timeout, the server erroring
 *
 * `missing` covers both endings a stranger could learn something from, and it
 * is one state on purpose: anybody can paste a string into a browser, and an
 * app that told expired and invalid apart would confirm that a link was real.
 * `completed` is separate because it is only ever reached by the person who
 * filled it in, and telling them their answers arrived is the whole point.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../lib/http";
import { FORMS_PREVIEW } from "../../../lib/site/config";
import { previewForm } from "../data/preconsultationPreview";
import { fetchPreconsultationForm } from "../services/preconsultationService";
import type { PreconsultationForm } from "../types/preconsultation.types";

export type PreconsultationStatus =
  | "loading"
  | "ready"
  | "completed"
  | "missing"
  | "failed";

export interface PreconsultationFormState {
  status: PreconsultationStatus;
  /** Set once the read has answered at all — the brand is drawn from it even
   *  on the two endings, so the page still belongs to the practitioner while
   *  it says the link is finished. */
  form?: PreconsultationForm;
  /** Runs the read again, for the Try again a failed state offers. */
  reload: () => void;
  /** Moves the page to `completed` once the answers have gone up: the token is
   *  spent, and a reload from here has to say so rather than reopening a form
   *  that can no longer be sent. */
  markCompleted: () => void;
}

export function usePreconsultationForm(
  handle: string,
  token: string
): PreconsultationFormState {
  const [status, setStatus] = useState<PreconsultationStatus>("loading");
  const [form, setForm] = useState<PreconsultationForm>();
  // Bumped by `reload`, which is the whole of what re-runs the effect.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Nothing to ask about, and the same answer as a token nobody minted:
    // either the route matched on an empty segment, or the address bar names
    // no practitioner — which is what a form link opened on a shared host or
    // on plain localhost is, since the read is made under the slug.
    if (!token.trim() || !handle.trim()) {
      setStatus("missing");
      return;
    }

    if (FORMS_PREVIEW) {
      const preview = previewForm(token);
      setForm(preview);
      setStatus(
        preview.state === "COMPLETED"
          ? "completed"
          : preview.state === "EXPIRED"
          ? "missing"
          : "ready"
      );
      return;
    }

    const controller = new AbortController();
    setStatus("loading");

    fetchPreconsultationForm(handle, token, controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) return;
        setForm(loaded);
        setStatus(
          loaded.state === "COMPLETED"
            ? "completed"
            : loaded.state === "EXPIRED"
            ? "missing"
            : "ready"
        );
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        // A 404 is the ordinary end of a single-use link rather than a fault,
        // so it is not logged.
        const gone = error instanceof ApiError && error.status === 404;
        if (!gone) console.error("Pre-consultation form failed to load:", error);
        setStatus(gone ? "missing" : "failed");
      });

    return () => controller.abort();
  }, [handle, token, attempt]);

  const reload = useCallback(() => setAttempt((run) => run + 1), []);
  const markCompleted = useCallback(() => setStatus("completed"), []);

  return { status, form, reload, markCompleted };
}

export default usePreconsultationForm;
